import { VALUE_TYPE } from 'src/components/ui/FilterPlate/constants';

const isNullOp = (op) => {
	if (!op) return false;

	return op === 'isNull' || op === 'isNotNull';
};

const handleNullOp = (op) => {
	if (op === 'isNull') return true;
	if (op === 'isNotNull') return false;

	throw new Error('Something went wrong in handleNullOp');
};

const baseTransformHandle = (data, op = null) => {
	if (isNullOp(op)) return handleNullOp(op);

	return data.value;
};

const parseNullOrString = (val, op) => {
	if (op === 'isNull') return val === 'true';

	return String(val);
};

const parseNullOrNumber = (val, op) => {
	if (op === 'isNull') return val === 'true';
	if (op === 'range') return val;

	return Number.parseInt(val, 10) || 0;
};

const parseNullOrBoolean = (val) => val === 'true';

const transformList = (data, op = null) => {
	if (isNullOp(op)) return handleNullOp(op);

	if (Array.isArray(data.value)) {
		return data.value.map(String).join(',');
	}

	return String(data.value ?? '');
};

const parseStringList = (val, op) => {
	if (op === 'isNull') return val === 'true';
	if (!val) return [];

	return String(val)
		.split(',')
		.map((item) => item.trim())
		.filter(Boolean);
};

const parseNumberList = (val, op) => {
	if (op === 'isNull') return val === 'true';
	if (!val) return [];

	return String(val)
		.split(',')
		.map((item) => Number(item))
		.filter((item) => Number.isFinite(item));
};

const dateConfig = {
	valueType: VALUE_TYPE.date,
	transform(data, op) {
		if (['lastDate', 'gtDate', 'ltDate'].includes(op)) {
			if (typeof data.value === 'string' && data.value.startsWith('P')) {
				return data.value;
			}

			return this.transformDuration(data.value.text + data.value.unit);
		}

		if (op === 'rangeDate' && data?.value) {
			return data.value;
		}

		return null;
	},
	parse: (val) => val,
	transformDuration: (input) => {
		if (typeof input !== 'string') return undefined;

		const match = input.match(/^(\d+)([a-zA-Z]+)$/);

		if (!match) return undefined;

		const value = Number(match[1]);
		const unit = match[2];

		if (!Number.isFinite(value) || value <= 0) return undefined;

		const unitMap = {
			seconds: 'S',
			minutes: 'M',
			hours: 'H',
			days: 'D',
			weeks: 'W',
			months: 'M',
			years: 'Y',
		};

		const isoUnit = unitMap[unit];

		if (!isoUnit) return undefined;

		if (['seconds', 'minutes', 'hours'].includes(unit)) {
			return `PT${value}${isoUnit}`;
		}

		return `P${value}${isoUnit}`;
	},
};

const stringConfig = {
	valueType: VALUE_TYPE.string,
	transform: baseTransformHandle,
	parse: parseNullOrString,
};

const numberConfig = {
	valueType: VALUE_TYPE.number,
	transform: baseTransformHandle,
	parse: parseNullOrNumber,
};

const booleanConfig = {
	valueType: VALUE_TYPE.boolean,
	transform: baseTransformHandle,
	parse: parseNullOrBoolean,
};

const stringListConfig = {
	valueType: VALUE_TYPE.enum,
	transform: transformList,
	parse: parseStringList,
};

const numberListConfig = {
	valueType: VALUE_TYPE.enum,
	transform: transformList,
	parse: parseNumberList,
};

const assign = (codes, config) =>
	Object.fromEntries(codes.map((code) => [code, config]));

export const FILTER_CONFIG = {
	...assign(
		[
			'cityName',
			'name',
			'companyName',
			'createdByName',
			'typeName',
			'comment',
			'externalId',
			'from',
			'to',
			'callType',
			'callStatus',
			'tag',
			'record',
			'manager',
		],
		stringConfig,
	),
	...assign(
		[
			'gorkoId',
			'companyGorkoId',
			'worthValue',
			'id',
			'value',
			'unsolvedObjectionsCount',
			'duration',
		],
		numberConfig,
	),
	...assign(['required', 'solved'], booleanConfig),
	...assign(
		[
			'updatedAt',
			'createdAt',
			'statusAt',
			'latestUnsolvedObjectionCreatedAt',
			'solvedAt',
			'postponedUntil',
		],
		dateConfig,
	),
	...assign(
		[
			'gorkoStatus',
			'salesTarget',
			'commissionPriority',
			'itemEntityType',
			'wedding',
			'birthday',
			'corporative',
			'newYear',
			'graduation',
		],
		stringListConfig,
	),
	...assign(['status', 'createdBy', 'type'], numberListConfig),
};

export const getFilterConfig = (code) => FILTER_CONFIG[code];
